import type {
  ExtendedResource,
  ExtendedResourceConstructor,
} from '@seen/core/ExtendedResource'
import type { QueryTypes } from '@seen/core/search'
import type { UseQueryResult } from '@tanstack/react-query'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useQuery } from '#src/hooks/useQuery.ts'
import { searchOptions } from '#src/query-options/searchOptions.ts'

export function useSearch<T extends ExtendedResource>(
  resourceClass: ExtendedResourceConstructor<T>,
  query?: QueryTypes,
  options: {
    /* Suppress the error toast for a search the user did not initiate, where a
     * failure is meant to drop what it feeds rather than be reported. The
     * console.error is still emitted. Mirrors `UseReadOptions`. */
    silentErrors?: boolean
    // `placeholderData` lets filter-driven lists keep the prior rows while the
    // next query loads (e.g. `keepPreviousData`) instead of dropping to an
    // empty/loading state. Picked from `searchOptions` so it carries the exact
    // query-key type.
    query?: Pick<
      ReturnType<typeof searchOptions<T>>,
      'queryFn' | 'placeholderData'
    > & {
      enabled?: boolean
    }
  } = {}
): UseQueryResult<T[]> {
  const medplum = useMedplum()
  const { queryFn: overrideQueryFn, ...inputQueryOptions } =
    options?.query ?? {}

  const queryOptions = searchOptions(medplum, resourceClass, query)

  const queryResult = useQuery({
    ...inputQueryOptions,
    ...queryOptions,
    queryFn: query && overrideQueryFn ? overrideQueryFn : queryOptions.queryFn,
  })
  const searchKey = queryOptions.queryKey.at(-1)
  useErrorToast(
    queryResult.error &&
      `Error accessing ${searchKey}: ${queryResult.error.message ?? 'Unknown error'}`,
    { silent: options.silentErrors }
  )
  return queryResult
}
